/**
 * Node visual para gerar link de agendamento online no Flow Builder
 * Exibe configuração do tipo de evento, origem do deal e variável de saída
 * 
 * RELEASE: 1.0.0 - 2026-02-18 - Novo node CRM agendamento online
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { CalendarClock, Link, Zap, Variable, Users } from 'lucide-react';
import { FlowNodeData, CrmSchedulingLinkConfig } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Retorna texto legível da origem do deal
 */
const getDealSelectionLabel = (
  dealSelection?: string,
  dealVariableName?: string
): string => {
  switch (dealSelection) {
    case 'from_trigger':
      return 'Do negócio no gatilho CRM';
    case 'from_variable':
      return dealVariableName ? `Da variável {{${dealVariableName}}}` : 'De uma variável';
    case 'most_recent':
      return 'Negócio mais recente do contato';
    default:
      return 'Do negócio no gatilho CRM';
  }
};

/**
 * Ícone da origem do deal
 */
const getDealSelectionIcon = (dealSelection?: string) => {
  switch (dealSelection) {
    case 'from_trigger':
      return Zap;
    case 'from_variable':
      return Variable;
    case 'most_recent':
      return Users;
    default:
      return Zap;
  }
};

/**
 * Componente visual do node Link de Agendamento Online
 * Segue padrão do CrmCreateDealNode (handles, toolbar, estrutura)
 */
export function CrmSchedulingLinkNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  const config = nodeData.config as CrmSchedulingLinkConfig | undefined;
  
  // Verifica se node está configurado (precisa de event_type_id e output_variable)
  const isConfigured = !!config?.event_type_id && !!config?.output_variable;
  
  // Ícone da origem do deal
  const DealIcon = getDealSelectionIcon(config?.deal_selection);

  return (
    <>
      {/* Toolbar flutuante para edição/exclusão */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={nodeData.onEdit as (() => void) | undefined}
        onDuplicate={(nodeData as any).onDuplicate as (() => void) | undefined}
        onDelete={nodeData.onDelete as (() => void) | undefined}
        onTestFromHere={(nodeData as any).onTestFromHere as (() => void) | undefined}
        isVisible={selected}
      />
      
      {/* Handle de entrada (topo) */}
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-cyan-500 !border-2 !border-white"
      />

      {/* Corpo do node */}
      <div className={`
        min-w-[220px] max-w-[280px] bg-card rounded-lg border-2 shadow-lg
        ${selected ? 'border-cyan-500 ring-2 ring-cyan-500/30' : 'border-cyan-400'}
        transition-all duration-200
      `}>
        {/* Header */}
        <div className="flex items-center gap-2 p-3 border-b border-border bg-cyan-500/10 rounded-t-lg">
          <div className="p-1.5 rounded bg-cyan-500">
            <CalendarClock className="h-4 w-4 text-white" />
          </div>
          <span className="font-semibold text-sm">{nodeData.config?.nickname || 'Link Agendamento'}</span>
        </div>

        {/* Conteúdo */}
        <div className="p-3 space-y-2 text-xs">
          {isConfigured ? (
            <>
              {/* Tipo de evento selecionado */}
              <div className="flex items-center gap-1.5">
                {config?.event_type_color && (
                  <span
                    className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0"
                    style={{ backgroundColor: config.event_type_color }}
                  />
                )}
                <span className="font-medium truncate" title={config?.event_type_title}>
                  {config?.event_type_title}
                </span>
                {config?.event_type_duration_minutes && (
                  <span className="text-muted-foreground flex-shrink-0">
                    {config.event_type_duration_minutes} min
                  </span>
                )}
              </div>

              {/* Origem do deal */}
              <div className="flex items-center gap-1.5 text-muted-foreground border-t border-border/50 pt-1.5">
                <DealIcon className="h-3 w-3 flex-shrink-0" />
                <span className="truncate" title={getDealSelectionLabel(config?.deal_selection, config?.deal_variable_name)}>
                  {getDealSelectionLabel(config?.deal_selection, config?.deal_variable_name)}
                </span>
              </div>

              {/* Variável de saída com destaque */}
              <div className="flex items-center gap-1.5 text-cyan-600 dark:text-cyan-400 border-t border-border/50 pt-1.5">
                <Link className="h-3 w-3 flex-shrink-0" />
                <span>→ Salva link em:</span>
                <code className="bg-cyan-100 dark:bg-cyan-900/30 px-1 rounded text-[10px]">
                  {`{{${config?.output_variable}}}`}
                </code>
              </div>
            </>
          ) : (
            <p className="text-muted-foreground italic text-center py-2">
              Clique para configurar
            </p>
          )}
        </div>
      </div>

      {/* Handle de saída (base) */}
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-cyan-500 !border-2 !border-white"
      />
    </>
  );
}
